import React from 'react';
import { ISubscription } from '@/types/subscription-management.types';
import { BillingCycle } from '@/constant';
import { formatFinnishCurrency } from '@/utils/Currency';

interface SubscriptionPlansCardProps {
  subscriptions: ISubscription[];
  loading?: boolean;
  currentPlanId?: string | null;
  onUpgrade?: (planId: string) => void;
}

const SubscriptionPlansCard: React.FC<SubscriptionPlansCardProps> = ({
  subscriptions,
  loading = false,
  currentPlanId = null,
  onUpgrade,
}) => {
  // Convert HTML string to plain text by stripping tags and decoding entities
  const toPlainText = (html: unknown): string => {
    if (typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  // Map numeric billing_cycle to user-friendly label
  const billingCycleLabel = (val: unknown): string => {
    const num = typeof val === 'number' ? val : Number(val);
    if (num === BillingCycle.MONTHLY) return 'Monthly';
    if (num === BillingCycle.YEARLY) return 'Yearly';
    if (num === BillingCycle.FIVE_DAYS) return '5 Days';
    return '-';
  };

  if (loading) {
    return (
      <div className="text-center py-8" role="status" aria-live="polite">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
        <p className="mt-2 text-gray-600 text-sm sm:text-base">
          Loading subscription plans...
        </p>
      </div>
    );
  }

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center">
        <p className="text-gray-500 text-sm sm:text-base">
          No subscription plans available
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
      {subscriptions.map((subscription) => {
        const isCurrentPlan = currentPlanId === subscription.id;

        return (
          <div
            key={subscription.id}
            className={`relative flex flex-col rounded-xl border-2 hover:shadow-xl ${
              isCurrentPlan
                ? 'border-green-500 bg-gradient-to-br shadow-lg'
                : subscription.is_most_popular
                  ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-white shadow-md hover:border-blue-600'
                  : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Badge Section */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 z-10">
              {isCurrentPlan && (
                <div className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-gradient-to-r from-green-500 to-green-600 px-3 py-1 sm:px-4 sm:py-1.5 shadow-md">
                  <svg
                    className="h-3 w-3 sm:h-4 sm:w-4 text-white flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs font-bold text-white whitespace-nowrap">
                    Current Plan
                  </span>
                </div>
              )}

              {!isCurrentPlan && subscription.is_most_popular && (
                <div className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1 sm:px-4 sm:py-1.5 shadow-md">
                  <svg
                    className="h-3 w-3 sm:h-4 sm:w-4 text-white flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs font-bold text-white whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}
            </div>

            {/* Card Content */}
            <div className="flex flex-col flex-grow p-4 sm:p-6 pt-6 sm:pt-8">
              {/* Plan Name */}
              <div className="mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                  {toPlainText(subscription.name)}
                </h3>
                {subscription.name_sub_title && (
                  <p className="mt-1 text-xs sm:text-sm text-gray-600 break-words">
                    {toPlainText(subscription.name_sub_title)}
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="mb-4 sm:mb-6">
                {subscription.price !== undefined &&
                subscription.price !== null ? (
                  <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
                    <span className="text-3xl sm:text-4xl font-extrabold text-gray-900">
                      {formatFinnishCurrency(subscription.price as number)}
                    </span>
                    {subscription.billing_cycle && (
                      <span className="text-sm sm:text-base text-gray-600 font-medium">
                        / {billingCycleLabel(subscription.billing_cycle)}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500 text-xs sm:text-sm">
                    Price not available
                  </span>
                )}
              </div>

              {/* Description */}
              {subscription.description && (
                <div className="mb-3 sm:mb-4 flex-grow">
                  <p className="text-xs sm:text-sm text-gray-700 line-clamp-3 sm:line-clamp-4 break-words">
                    {toPlainText(subscription.description)}
                  </p>
                </div>
              )}

              {/* Status Badge */}
              <div className="mb-3 sm:mb-4">
                <span
                  className={`inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-semibold ${
                    subscription.is_active
                      ? `bg-green-100 dark:bg-green-800 contrast:bg-green-800 
                  text-green-800 dark:text-green-200 contrast:text-green-200 
                    border border-green-200 dark:border-green-800 contrast:border-green-800`
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}
                >
                  {subscription.is_active ? (
                    <>
                      <svg
                        className="w-3 h-3 mr-1 sm:mr-1.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Active
                    </>
                  ) : (
                    'Inactive'
                  )}
                </span>
              </div>

              {/* Upgrade Button (only for non-current plans) */}
              {!isCurrentPlan && onUpgrade && (
                <div className="mt-auto pt-3 sm:pt-4 border-t border-gray-200">
                  <button
                    onClick={() => onUpgrade(subscription.id)}
                    className={`w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-md hover:shadow-lg ${
                      subscription.is_most_popular
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                        : 'bg-gradient-to-r from-gray-700 dark:from-gray-200 contrast:from-gray-200 dark:to-gray-300 contrast:to-gray-300 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white'
                    }`}
                    aria-label={`Upgrade to This Plan: ${toPlainText(subscription.name)}`}
                  >
                    <span className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                      <span className="whitespace-nowrap contrast:text-white">
                        Upgrade to This Plan
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {/* Current Plan Indicator */}
              {isCurrentPlan && (
                <div className="mt-auto pt-3 sm:pt-4 border-t border-green-300">
                  <div className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg bg-green-600 contrast:bg-green-900 text-white text-center font-semibold text-xs sm:text-sm shadow-md">
                    <span className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="whitespace-nowrap">Active Plan</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionPlansCard;
